// @flow
import * as React from 'react';
import deepEqual from 'react-fast-compare';
import styled from 'styled-components';
import { camelCaseToDash } from 'utils/string';
import { identity, isDefined } from 'utils/functional';
import Text, { BodySmall } from 'components/Text';
import Box from 'components/Box';
import ErrorIcon from 'error.svg';
import { FormContextConsumer } from './FormContext';
import { colors, transition, textStyles, breakpoints } from '../../theme';

const { useEffect, useState, useRef, useCallback } = React;

export const getQaId = (name: string) =>
  camelCaseToDash(name).replace(/\./g, '-');

const FIELD_MARGIN = 30;

const BaseLabel = styled.label`
  ${textStyles.h5};
  ${props =>
    props.noWrap
      ? `
  @media (min-width: ${breakpoints[2]}) {
    white-space: nowrap;
    max-width: 560px;
  }`
      : ''}
`;

export const Label = styled(BaseLabel)`
  & + * {
    margin-top: 16px;
  }
`;

const InlineLabel = styled(BaseLabel)`
  position: absolute;
  top: 50%;
  left: 18px;
  transform: translateY(-50%);
  pointer-events: none;
  transform-origin: left top;
  font-size: 18px;
  font-weight: 400;
  letter-spacing: 0.4px;
  transition: all ${transition};
  ${props =>
    (props.hasFocus || props.hasValue) &&
    `
      transform: scale(0.625) translateY(-50%) translateY(-28px);
      opacity: 0.8;
    `}
`;

export const Description = styled(BodySmall)`
  margin-top: 0;
  color: ${colors.battleshipGrey};
  line-height: 1.375;
  ${props =>
    props.$noWrap
      ? `
  @media (min-width: ${breakpoints[2]}) {
    display: flex;
    & > div {
      flex: 1 0 auto;
      max-width: 720px;
    }
  }`
      : ''}
  + * {
    margin-top: 16px;
  }
`;

export const ErrorContainer = styled.div`
  display: flex;
  margin-top: 4px;
  font-size: 13px;
  color: ${colors.scarlet};
  ${props =>
    props.inlineLabel &&
    `
    position: absolute;
    top: 50%;
    right: 18px;
    transform: translateY(-50%);
    margin-top: 0;
  `}
  ${props =>
    props.noWrap
      ? `
  @media (min-width: ${breakpoints[2]}) {
    white-space: nowrap;
    max-width: 560px;
  }`
      : ''}
  svg {
    margin-top: 3px;
  }
`;

export const Error = ({
  errorMessage,
  inlineLabel,
  noWrap,
  qaId,
  ...props
}: {
  errorMessage: React.Node,
  inlineLabel?: boolean,
  noWrap?: boolean,
  qaId?: string
}) => (
  <ErrorContainer noWrap={noWrap} inlineLabel={inlineLabel} {...props}>
    <ErrorIcon />
    <Text pl="4px" data-qa-id={qaId ? `${qaId}-error` : undefined}>
      {errorMessage}
    </Text>
  </ErrorContainer>
);

export const FieldBox = styled(Box)`
  margin-bottom: ${FIELD_MARGIN}px;
`;

type Props<I> = {
  name: string,
  label?: React.Node,
  inlineLabel?: boolean,
  hidden?: boolean,
  description?: React.Node,
  renderInput: ({
    labelRef: { current: null | React$ElementRef<'label'> },
    values: Object,
    value: any,
    id: string,
    qaId: string,
    errors: APIErrors,
    hasError: boolean,
    hasSuccess: boolean,
    hasGeneralError: boolean,
    updateValue: any => void,
    updateValueWith: Function => void,
    updateValueByName: (string, any) => void,
    getValue: string => mixed,
    onBlur?: Function,
    onFocus?: Function
  }) => React.Node,
  mapError?: string => React.Node,
  matchGeneralError?: string,
  inputProps?: Object,
  customCompare?: (I, I) => boolean,
  customErrorMatch?: APIErrors => ?Object,
  noMargin?: boolean,
  noWrap?: boolean,
  success?: boolean,
  customError?: ?string
};

const checkDeep = (a: mixed, b: mixed): boolean => a === b || deepEqual(a, b);

const areEqual = (pp, np) => {
  const customComparison = np.customCompare ? np.customCompare(pp, np) : true;
  if (pp.name !== np.name) {
    return false;
  }
  const value = np.getValue(np.name);
  const prevValue = pp.getValue(pp.name);
  const deepChecks = ['errors', 'inputProps'];
  return (
    np.label === pp.label &&
    np.customError === pp.customError &&
    np.success === pp.success &&
    customComparison &&
    deepChecks.every(p => checkDeep(pp[p], np[p])) &&
    checkDeep(value, prevValue)
  );
};

type FieldInternalProps = {
  getValue: string => mixed,
  fieldQaIdPrefix: string,
  values: Object,
  labelRef: { current: null | React$ElementRef<'label'> | Function },
  updateValue: (string, mixed) => void,
  updateValueWith: (string, Function) => void,
  onFocus: Function,
  onBlur: Function,
  errors: APIErrors,
  FieldLabel: React.ElementType,
  hasFocus: boolean,
  name: string,
  label?: React.Node,
  inlineLabel?: boolean,
  hidden?: boolean,
  description?: React.Node,
  renderInput: ({
    labelRef: { current: null | React$ElementRef<'label'> },
    values: Object,
    value: any,
    id: string,
    qaId: string,
    errors: APIErrors,
    hasError: boolean,
    hasSuccess: boolean,
    hasGeneralError: boolean,
    updateValue: any => void,
    updateValueWith: Function => void,
    getValue: string => mixed,
    updateValueByName: (string, any) => void,
    onBlur?: Function,
    onFocus?: Function
  }) => React.Node,
  mapError: string => React.Node,
  matchGeneralError?: string,
  inputProps?: Object,
  customCompare?: (FieldInternalProps, FieldInternalProps) => boolean,
  customErrorMatch?: APIErrors => ?Object,
  noMargin?: boolean,
  noWrap?: boolean,
  success?: boolean,
  customError?: ?string
};

const FieldInternal = React.memo(
  ({
    getValue,
    fieldQaIdPrefix,
    renderInput,
    values,
    labelRef,
    errors,
    updateValue,
    updateValueWith,
    onFocus,
    onBlur,
    inlineLabel,
    hidden,
    name,
    label,
    description,
    matchGeneralError,
    FieldLabel,
    hasFocus,
    mapError,
    customErrorMatch,
    noMargin,
    noWrap,
    inputProps,
    success,
    customError
  }: FieldInternalProps) => {
    const qaId = fieldQaIdPrefix
      ? `${fieldQaIdPrefix}-${getQaId(name)}`
      : getQaId(name);
    const id = qaId;
    const generalError = errors[''] || errors.general;
    const hasGeneralError =
      isDefined(matchGeneralError) && generalError === matchGeneralError;

    const value = getValue(name);
    const inputPropsValue = inputProps && inputProps.value;
    useEffect(
      () => {
        if (inputPropsValue && inputPropsValue !== value) {
          updateValue(name, inputPropsValue);
        }
      },
      [inputPropsValue, name, updateValue, value]
    );

    const fieldError =
      customError ||
      (customErrorMatch ? customErrorMatch(errors) : errors[name]);
    const updateValueForField = useCallback(
      newValue => {
        updateValue(name, newValue);
      },
      [updateValue, name]
    );
    const i = renderInput({
      labelRef,
      name,
      values,
      value,
      id,
      qaId,
      errors,
      hasError: !!fieldError || hasGeneralError,
      hasSuccess: !!success,
      hasGeneralError: !!generalError && hasGeneralError,
      updateValue: updateValueForField,
      updateValueByName: updateValue,
      updateValueWith: newValue => updateValueWith(name, newValue),
      getValue,
      onFocus,
      onBlur
    });

    return (
      <Box
        position="relative"
        width="100%"
        mb={inlineLabel || hidden || noMargin ? undefined : FIELD_MARGIN}
        className={fieldError ? 'error' : null} // used to scroll to error
      >
        {label && (
          <FieldLabel
            htmlFor={id}
            ref={labelRef}
            id={`${id}-label`}
            inlineLabel={inlineLabel}
            hasFocus={hasFocus}
            hasValue={isDefined(value)}
            noWrap={noWrap}
          >
            {label}
          </FieldLabel>
        )}
        {description && (
          <Description $noWrap={noWrap}>
            <div>{description}</div>
          </Description>
        )}
        <Box>{i}</Box>
        {fieldError && (
          <ErrorContainer inlineLabel={inlineLabel} noWrap={noWrap}>
            <ErrorIcon />
            <Text pl="4px" data-qa-id={`${qaId}-error`}>
              {mapError(fieldError)}
            </Text>
          </ErrorContainer>
        )}
        {hasGeneralError && generalError && (
          <Error
            inlineLabel={inlineLabel}
            errorMessage={mapError(generalError)}
            qaId={qaId}
            noWrap={noWrap}
          />
        )}
      </Box>
    );
  },
  areEqual
);

const Field = ({
  name,
  label,
  inlineLabel,
  description,
  renderInput,
  mapError = identity,
  matchGeneralError,
  inputProps = {},
  customCompare,
  customErrorMatch,
  hidden,
  noMargin,
  noWrap,
  success,
  customError
}: Props<FieldInternalProps>) => {
  const [hasFocus, updateHasFocus] = useState(false);
  const onFocus = () => updateHasFocus(true);
  const onBlur = () => updateHasFocus(false);
  const labelRef = useRef(null);
  const FieldLabel = inlineLabel ? InlineLabel : Label;
  return (
    <FormContextConsumer>
      {({
        values,
        errors,
        getValue,
        updateValue,
        updateValueWith,
        fieldQaIdPrefix
      }) => (
        <FieldInternal
          values={values}
          errors={errors}
          getValue={getValue}
          updateValue={updateValue}
          updateValueWith={updateValueWith}
          fieldQaIdPrefix={fieldQaIdPrefix || ''}
          inlineLabel={inlineLabel}
          mapError={mapError}
          labelRef={labelRef}
          onFocus={onFocus}
          onBlur={onBlur}
          renderInput={renderInput}
          name={name}
          label={label}
          description={description}
          FieldLabel={FieldLabel}
          hasFocus={hasFocus}
          matchGeneralError={matchGeneralError}
          inputProps={inputProps}
          customCompare={customCompare}
          customErrorMatch={customErrorMatch}
          hidden={hidden}
          noMargin={noMargin}
          noWrap={noWrap}
          customError={customError}
          success={success}
        />
      )}
    </FormContextConsumer>
  );
};

export default Field;
